import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { resolveComponentProps, useSlotProps } from '@mui/base/utils';
import useForkRef from '@mui/utils/useForkRef';
import { PickersModalDialog } from '../../components/PickersModalDialog';
import { usePicker } from '../usePicker';
import { onSpaceOrEnter } from '../../utils/utils';
import { useUtils } from '../useUtils';
import { LocalizationProvider } from '../../../LocalizationProvider';
import { WrapperVariantContext } from '../../components/wrappers/WrapperVariantContext';
import { PickersViewLayout } from '../../components/PickersViewLayout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Hook managing all the single-date mobile pickers:
 * - MobileDatePicker
 * - MobileDateTimePicker
 * - MobileTimePicker
 */
export const useMobilePicker = ({
  props,
  valueManager,
  getOpenDialogAriaText,
  validator
}) => {
  const {
    components,
    componentsProps,
    className,
    format,
    readOnly,
    disabled,
    localeText
  } = props;
  const utils = useUtils();
  const inputRef = React.useRef(null);
  const {
    open,
    actions,
    layoutProps,
    renderCurrentView,
    fieldProps: pickerFieldProps
  } = usePicker({
    props,
    inputRef,
    valueManager,
    validator,
    autoFocusView: true,
    additionalViewProps: {},
    wrapperVariant: 'mobile'
  });
  const Field = components.Field;
  const fieldProps = useSlotProps({
    elementType: Field,
    externalSlotProps: componentsProps == null ? void 0 : componentsProps.field,
    additionalProps: _extends({}, pickerFieldProps, {
      readOnly: readOnly != null ? readOnly : true,
      disabled,
      className,
      format
    }),
    ownerState: props
  });
  const componentsPropsForField = _extends({}, fieldProps.componentsProps, {
    input: ownerState => {
      var _fieldProps$component;
      const externalInputProps = resolveComponentProps(componentsProps == null ? void 0 : componentsProps.input, ownerState);
      const inputPropsPassedByField = resolveComponentProps((_fieldProps$component = fieldProps.componentsProps) == null ? void 0 : _fieldProps$component.input, ownerState);
      return _extends({}, inputPropsPassedByField, externalInputProps, {
        disabled
      }, !(disabled || readOnly) && {
        onClick: actions.onOpen,
        onKeyDown: onSpaceOrEnter(actions.onOpen)
      }, {
        inputProps: _extends({
          'aria-label': getOpenDialogAriaText(pickerFieldProps.value, utils)
        }, inputPropsPassedByField == null ? void 0 : inputPropsPassedByField.inputProps, externalInputProps == null ? void 0 : externalInputProps.inputProps)
      });
    }
  });
  const componentsForField = _extends({}, fieldProps.components, {
    Input: components.Input
  });
  const handleInputRef = useForkRef(inputRef, fieldProps.inputRef);
  const renderPicker = () => /*#__PURE__*/_jsx(LocalizationProvider, {
    localeText: localeText,
    children: /*#__PURE__*/_jsxs(WrapperVariantContext.Provider, {
      value: "mobile",
      children: [/*#__PURE__*/_jsx(Field, _extends({}, fieldProps, {
        components: componentsForField,
        componentsProps: componentsPropsForField,
        inputRef: handleInputRef
      })), /*#__PURE__*/_jsx(PickersModalDialog, _extends({}, actions, {
        open: open,
        components: _extends({}, components, {
          // Avoids to render 2 action bar, will be removed once `PickersModalDialog` stop displaying the action bar.
          ActionBar: () => null
        }),
        componentsProps: _extends({}, componentsProps, {
          actionBar: undefined
        }),
        children: /*#__PURE__*/_jsx(PickersViewLayout, _extends({}, layoutProps, {
          components: components,
          componentsProps: componentsProps,
          children: renderCurrentView()
        }))
      }))]
    })
  });
  return {
    renderPicker
  };
};